import { isAttachingToTransaction, isProcessingOrErrorReceipt, isProcessingReceipt } from './receiptsPredicates';
import type { Receipt } from './receiptsTypes';

export type ReceiptProcessingSessionState = {
  activeDocumentIds: ReadonlyArray<string>;
  completedDocumentIds: ReadonlyArray<string>;
};

export type ReceiptProcessingSessionResult = ReceiptProcessingSessionState & {
  newlyCompletedDocumentIds: ReadonlyArray<string>;
  panelReceipts: ReadonlyArray<Receipt>;
};

/**
 * Reconciles the processing panel across receipt status updates. Persistence is deliberately left
 * to each platform so web can use sessionStorage while mobile can use its own session boundary.
 */
export const reconcileReceiptProcessingSession = (
  receipts: ReadonlyArray<Receipt>,
  previousState: ReceiptProcessingSessionState,
): ReceiptProcessingSessionResult => {
  const previouslyActiveDocumentIds = new Set(previousState.activeDocumentIds);
  const completedDocumentIds = new Set(previousState.completedDocumentIds);
  const { activeDocumentIds, loadedDocumentIds, newlyCompletedDocumentIds } = receipts.reduce(
    (result, receipt) => {
      const { documentId } = receipt;
      result.loadedDocumentIds.add(documentId);

      if (isProcessingReceipt(receipt) || isAttachingToTransaction(receipt)) {
        result.activeDocumentIds.add(documentId);
      } else if (
        receipt.status === 'READY' &&
        !receipt.targetTransactionId &&
        previouslyActiveDocumentIds.has(documentId) &&
        !completedDocumentIds.has(documentId)
      ) {
        completedDocumentIds.add(documentId);
        result.newlyCompletedDocumentIds.push(documentId);
      }

      return result;
    },
    {
      activeDocumentIds: new Set<string>(),
      loadedDocumentIds: new Set<string>(),
      newlyCompletedDocumentIds: [] as string[],
    },
  );

  // A restored session can reconcile before receipts load, so keep watching active IDs that are not loaded yet.
  previouslyActiveDocumentIds.forEach((documentId) => {
    if (!loadedDocumentIds.has(documentId)) {
      activeDocumentIds.add(documentId);
    }
  });

  return {
    activeDocumentIds: [...activeDocumentIds],
    completedDocumentIds: [...completedDocumentIds],
    newlyCompletedDocumentIds,
    panelReceipts: receipts.filter(
      (receipt) =>
        isProcessingOrErrorReceipt(receipt) ||
        (receipt.status === 'READY' && completedDocumentIds.has(receipt.documentId)),
    ),
  };
};
